import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import client from "../../../../api/client";
import { GlobalContext } from "../../../../context";
import { fmtDay, fmtTime } from "../../home/time";
import Icon from "../Icon";
import CohortProgress from "./CohortProgress";
import EmailComposer from "./EmailComposer";
import { CertificateProgress, WorkTable } from "../GradeBreakdown";
import { fromGrading } from "../progress";
import { Avatar, Bar, Pill, ScopePicker, STATUS, useScopes } from "./staffUi";

const LETTER = { A: 95, B: 85, C: 75, D: 65, F: 40 };
const pct = (n, d) => (d ? Math.round((n / d) * 100) : null);
const avg = (xs) => { const v = xs.filter((x) => x != null); return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null; };
const ago = (n) => (n == null ? "No activity yet" : n === 0 ? "Today" : n === 1 ? "Yesterday" : `${n} days ago`);
const CAP = { passed: ["ok", "Passed"], submitted: ["brand", "Submitted"], redo_required: ["risk", "Redo required"], not_submitted: ["gray", "Not submitted"], left_out: ["watch", "Left out by team"] };

function AttBar({ a }) {
  const held = a.live + a.recording + a.absent;
  return (
    <div className="sp-bar">
      <div className="t"><b>{a.percent == null ? "—" : `${a.percent}%`}</b><span>{pct(a.live, held) ?? 0}% live · {pct(a.recording, held) ?? 0}% rec</span></div>
      <div className="sp-track"><i className="live" style={{ width: `${pct(a.live, held) || 0}%` }} /><i className="rec" style={{ width: `${pct(a.recording, held) || 0}%` }} /><i className="pass" /></div>
    </div>
  );
}

/** Per class: joined live / watched the recording / absent, stacked to 100%. */
function AttendanceChart({ data }) {
  const n = data.classes.length, total = data.students.length;
  if (!n || !total) return <p className="course-tag" style={{ margin: 0 }}>Attendance shows here once classes have been held.</p>;
  const W = 760, H = 240, L = 36, R = 10, T = 12, B = 30, bw = (W - L - R) / n;
  const y = (v) => T + (H - T - B) * (1 - v / 100);
  const step = Math.ceil(n / 14);
  return (
    <svg className="sp-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Attendance per class: live, recording and absent">
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--line)" /><text x={L - 6} y={y(v) + 3} textAnchor="end">{v}%</text></g>
      ))}
      {data.classes.map((c, i) => {
        const live = pct(c.live, total), rec = pct(c.recording, total);
        const x = L + i * bw + bw * 0.16, w = bw * 0.68;
        return (
          <g key={c.id}>
            <rect x={x} y={y(live)} width={w} height={y(0) - y(live)} fill="var(--brand)"><title>{`${c.title} · ${live}% joined live`}</title></rect>
            <rect x={x} y={y(live + rec)} width={w} height={y(live) - y(live + rec)} fill="var(--blue)"><title>{`${c.title} · ${rec}% watched the recording`}</title></rect>
            <rect x={x} y={y(100)} width={w} height={y(live + rec) - y(100)} fill="#c9ced8"><title>{`${c.title} · ${100 - live - rec}% absent`}</title></rect>
            {i % step === 0 && <text x={x + w / 2} y={H - B + 15} textAnchor="middle">{fmtDay(c.start_time).split(" ").slice(1).join(" ")}</text>}
          </g>
        );
      })}
      <line x1={L} x2={W - R} y1={y(70)} y2={y(70)} stroke="var(--ink)" strokeDasharray="4 4" opacity="0.45" />
      <text x={W - R} y={y(70) - 5} textAnchor="end" style={{ fill: "var(--ink)" }}>70% needed</text>
    </svg>
  );
}

function Overview({ data, onOpen, goStudents }) {
  const S = data.students;
  const risk = S.filter((s) => s.status === "risk"), watch = S.filter((s) => s.status === "watch");
  const last = data.classes.length - 1;
  return (
    <>
      <div className="sp-kpis">
        <div className="sp-kpi"><span className="k">Students</span><span className="v">{S.length}</span><span className="n">{data.classes.length} classes held</span></div>
        <div className="sp-kpi"><span className="k">Average attendance</span><span className="v">{avg(S.map((s) => s.attendance.percent)) ?? "—"}{S.length && data.classes.length ? "%" : ""}</span>
          <span className="n">{avg(S.map((s) => pct(s.attendance.live, data.classes.length))) ?? 0}% live · {avg(S.map((s) => pct(s.attendance.recording, data.classes.length))) ?? 0}% recording</span></div>
        <div className="sp-kpi"><span className="k">Absent last class</span><span className="v">{last >= 0 ? S.filter((s) => s.classes[last] === "absent").length : "—"}</span><span className="n">{last >= 0 ? data.classes[last].title : "No classes yet"}</span></div>
        <div className="sp-kpi"><span className="k">Activities score</span><span className="v">{avg(S.map((s) => s.activity)) ?? "—"}{avg(S.map((s) => s.activity)) != null ? "%" : ""}</span><span className="n">Quizzes 40% + assignments 60%, work past its deadline</span></div>
        <div className="sp-kpi hot"><span className="k">Ready for certificate</span><span className="v">{S.filter((s) => s.ready && !s.certificate?.approved).length}</span><span className="n">Met every requirement</span></div>
        <div className="sp-kpi alert"><span className="k">At risk</span><span className="v">{risk.length}</span><span className="n">{watch.length} more to watch</span></div>
      </div>
      <div className="sp-two">
        <section className="sp-box">
          <div className="sp-box-h"><h2>Attendance per class</h2><div className="sp-legend"><span><i style={{ background: "var(--brand)" }} />Joined live</span><span><i style={{ background: "var(--blue)" }} />Watched the recording</span><span><i style={{ background: "#c9ced8" }} />Absent</span></div></div>
          <div className="sp-box-b"><AttendanceChart data={data} /></div>
        </section>
        <section className="sp-box">
          <div className="sp-box-h"><h2>Needs attention</h2><small>{risk.length + watch.length} students</small></div>
          <div className="sp-rows">
            {[...risk, ...watch].slice(0, 8).map((s) => (
              <button key={s.id} type="button" onClick={() => onOpen(s.id)}><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.reasons.join(" · ")}</small></div><Pill cls={STATUS[s.status][0]}>{STATUS[s.status][1]}</Pill></button>
            ))}
            {!risk.length && !watch.length && <div><span /><small>{S.length ? "Everyone is on track." : "No students in this course yet."}</small><span /></div>}
          </div>
          <div className="sp-foot"><span>At risk: two or more problems, or missed the last 3 classes.</span><button className="link-btn" type="button" onClick={goStudents}>See all ›</button></div>
        </section>
      </div>
    </>
  );
}

function Students({ data, onOpen, filter, setFilter, onEmail }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "status", dir: 1 });
  const counts = { all: data.students.length, risk: 0, watch: 0, ok: 0 };
  data.students.forEach((s) => counts[s.status]++);
  const key = { status: (s) => ({ risk: 0, watch: 1, ok: 2 }[s.status]), name: (s) => s.name, att: (s) => s.attendance.percent ?? -1, act: (s) => s.activity ?? -1, sub: (s) => s.submitted, pts: (s) => s.points, last: (s) => s.idle_days ?? 9999 }[sort.key];
  const rows = data.students.filter((s) => (filter === "all" || s.status === filter) && `${s.name} ${s.email}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (key(a) > key(b) ? 1 : key(a) < key(b) ? -1 : 0) * sort.dir);
  const th = (k, l, cls) => (
    <th className={cls} aria-sort={sort.key === k ? (sort.dir > 0 ? "ascending" : "descending") : undefined}>
      <button type="button" onClick={() => setSort({ key: k, dir: sort.key === k ? -sort.dir : 1 })}>{l}{sort.key === k ? (sort.dir > 0 ? " ↑" : " ↓") : ""}</button>
    </th>
  );
  return (
    <section className="sp-box">
      <div className="sp-tools">
        <input type="search" placeholder="Search name or email" aria-label="Search students" value={q} onChange={(e) => setQ(e.target.value)} />
        <div className="lb-tabs chips-row" role="group" aria-label="Status">
          {[["all", "All"], ["risk", "At risk"], ["watch", "Watch"], ["ok", "On track"]].map(([k, l]) => <button key={k} type="button" aria-pressed={filter === k} onClick={() => setFilter(k)}>{l} · {counts[k]}</button>)}
        </div>
        {rows.length > 0 && <button className="cbtn quiet" type="button" onClick={() => onEmail(rows)}>Email {filter === "all" && !q ? "everyone" : `these ${rows.length}`}</button>}
      </div>
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead><tr>{th("name", "Student")}{th("status", "Status")}{th("att", "Attendance")}{th("act", "Activities")}{th("sub", "Assignments", "num")}<th>Capstone</th>{th("pts", "Points", "num")}{th("last", "Last active")}</tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="click" tabIndex={0} onClick={() => onOpen(s.id)} onKeyDown={(e) => e.key === "Enter" && onOpen(s.id)}>
                <td><div className="sp-stu"><Avatar s={s} /><div><strong>{s.name}</strong><small>{s.email}</small></div></div></td>
                <td><Pill cls={STATUS[s.status][0]}>{STATUS[s.status][1]}</Pill></td>
                <td><AttBar a={s.attendance} /></td>
                <td><Bar value={s.activity} /></td>
                <td className="num">{s.submitted} / {data.assignments.length}<div className="course-tag" style={{ fontSize: 12 }}>{s.graded} graded</div></td>
                <td><Pill cls={CAP[s.capstone][0]}>{CAP[s.capstone][1]}</Pill></td>
                <td className="num">{s.points}</td>
                <td style={s.idle_days >= 7 ? { color: "#c0392b", fontWeight: 500 } : { color: "var(--muted)" }}>{ago(s.idle_days)}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="course-tag">No students match.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="sp-foot"><span>A class counts as attended when the student joins live or watches 60% of the recording. The mark on each bar is the 70% attendance requirement.</span></div>
    </section>
  );
}

// Monday (WAT) of the week a class falls in, as "YYYY-MM-DD" — classes are planned in WAT weeks.
const weekOf = (iso) => {
  const d = new Date(new Date(iso).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" }) + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};
const dayLabel = (ymd, add = 0) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + add); return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", timeZone: "UTC" }); };

/** Classes held, one week at a time (some courses run many classes a week). Opens on the latest week. */
/** Our attendance vs Zoom's participant report for one class: who doesn't line up. */
function zoomCheck(data, i) {
  const c = data.classes[i];
  if (!c.zoom) return null;
  const notInZoom = [], notMarked = [], short = [];
  data.students.forEach((s) => {
    const mark = s.classes[i], mins = s.zoom?.[i] ?? 0;
    if (mark === "live" && mins === 0) notInZoom.push(s);
    else if (mark === "absent" && mins > 0) notMarked.push({ s, mins });
    else if (mark === "live" && mins < c.minutes / 2) short.push({ s, mins });
  });
  return { notInZoom, notMarked, short, issues: notInZoom.length + notMarked.length + short.length };
}

function Classes({ data }) {
  const weeks = useMemo(() => [...new Set(data.classes.map((c) => weekOf(c.start_time)))], [data.classes]);
  const [w, setW] = useState(weeks.length - 1);
  const wk = weeks[w];
  const items = data.classes.map((c, i) => ({ c, i })).filter(({ c }) => weekOf(c.start_time) === wk);
  const [openZoom, setOpenZoom] = useState(null);
  return (
    <section className="sp-box">
      <div className="sp-box-h">
        {weeks.length ? (
          <div className="sp-wknav">
            <button className="cbtn quiet" type="button" onClick={() => setW(w - 1)} disabled={w <= 0} aria-label="Previous week">‹</button>
            <div><b>{w === weeks.length - 1 ? "Latest week" : `Week of ${dayLabel(wk)}`}</b><small>{dayLabel(wk)} – {dayLabel(wk, 6)} · {items.length} {items.length === 1 ? "class" : "classes"}</small></div>
            <button className="cbtn quiet" type="button" onClick={() => setW(w + 1)} disabled={w >= weeks.length - 1} aria-label="Next week">›</button>
            {w !== weeks.length - 1 && <button className="link-btn" type="button" onClick={() => setW(weeks.length - 1)}>Back to latest week</button>}
          </div>
        ) : <h2>Classes held</h2>}
        <div className="sp-legend"><span><i style={{ background: "var(--brand)" }} />Live</span><span><i style={{ background: "var(--blue)" }} />Recording</span><span><i style={{ background: "#c9ced8" }} />Absent</span></div>
      </div>
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead><tr><th>Class</th><th>Date (your time)</th><th className="num">Live</th><th className="num">Recording</th><th className="num">Absent</th><th>Zoom</th><th>Each student</th></tr></thead>
          <tbody>
            {items.map(({ c, i }) => {
              const absent = data.students.length - c.live - c.recording;
              const z = zoomCheck(data, i);
              return (
                <Fragment key={c.id}>
                <tr>
                  <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{c.title}</strong></td>
                  <td>{fmtDay(c.start_time)} · {fmtTime(c.start_time)}</td>
                  <td className="num">{c.live}</td><td className="num">{c.recording}</td>
                  <td className="num" style={absent > data.students.length / 4 ? { color: "#c0392b", fontWeight: 700 } : undefined}>{absent}</td>
                  <td>{!c.zoom ? <span className="course-tag" style={{ fontSize: 12.5 }}>No Zoom report</span>
                    : <button type="button" className="link-btn" aria-expanded={openZoom === c.id} onClick={() => setOpenZoom(openZoom === c.id ? null : c.id)}>
                        {c.zoom.students} in Zoom{z.issues ? ` · ${z.issues} to check` : " · matches"}</button>}</td>
                  <td><div className="sp-dots">{data.students.map((s) => <i key={s.id} className={`sp-dot ${s.classes[i]}`} title={`${s.name}: ${{ live: "live", staff: "marked by staff", rec: "recording", absent: "absent" }[s.classes[i]]}${s.zoom?.[i] != null ? ` · Zoom ${s.zoom[i]} min` : ""}`} />)}</div></td>
                </tr>
                {openZoom === c.id && z && (
                  <tr className="sp-zoomrow"><td colSpan={7}>
                    <div className="sp-zoomcheck">
                      <div><b>Marked attended, not in Zoom · {z.notInZoom.length}</b>{z.notInZoom.length ? z.notInZoom.map((s) => <span key={s.id}>{s.name}</span>) : <small>None</small>}</div>
                      <div><b>In Zoom, not marked · {z.notMarked.length}</b>{z.notMarked.length ? z.notMarked.map(({ s, mins }) => <span key={s.id}>{s.name} · {mins} min</span>) : <small>None</small>}</div>
                      <div><b>Under half the class in Zoom · {z.short.length}</b>{z.short.length ? z.short.map(({ s, mins }) => <span key={s.id}>{s.name} · {mins} of {c.minutes} min</span>) : <small>None</small>}</div>
                      <div><b>In Zoom, not matched to a student · {c.zoom.unmatched.length}</b>{c.zoom.unmatched.length ? c.zoom.unmatched.map((p, k) => <span key={k}>{p.name} · {p.minutes} min</span>) : <small>None</small>}<small>Staff, guests, or students using a different name or email in Zoom.</small></div>
                    </div>
                  </td></tr>
                )}
                </Fragment>
              );
            })}
            {!data.classes.length && <tr><td colSpan={7} className="course-tag">No classes held yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="sp-foot"><span>Absent means the student neither joined live nor watched 60% of the recording. Watching later still changes it to attended. Zoom figures come from Zoom's participant report after class and are for comparison only.</span></div>
    </section>
  );
}

function Activities({ data }) {
  const total = data.students.length;
  const rows = [
    ...data.assignments.map((a, i) => {
      const subs = data.students.map((s) => s.assignments[i]).filter(Boolean);
      const graded = subs.filter((x) => LETTER[x.grade] != null);
      return { key: `a${a.id}`, title: a.title, kind: `Assignment${a.due_at ? ` · due ${fmtDay(a.due_at)}` : ""}`, done: subs.length, avg: avg(graded.map((x) => LETTER[x.grade])), pass: pct(graded.filter((x) => LETTER[x.grade] >= 70).length, graded.length), wait: subs.length - graded.length };
    }),
    ...data.quizzes.map((qz, i) => {
      const sc = data.students.map((s) => s.quizzes[i]).filter((v) => v != null);
      return { key: `q${qz.id}`, title: qz.title, kind: "Quiz", done: sc.length, avg: avg(sc), pass: pct(sc.filter((v) => v >= qz.pass).length, sc.length), wait: 0 };
    }),
  ];
  return (
    <section className="sp-box">
      <div className="sp-box-h"><h2>Quizzes and assignments</h2><small>Letter grades count as A 95, B 85, C 75, D 65, F 40 points. Activities = quiz average × 40% + assignment average × 60%</small></div>
      <div className="sp-table-wrap">
        <table className="sp-table">
          <thead><tr><th>Activity</th><th>Done</th><th>Average score</th><th className="num">Pass rate</th><th>Grading</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td><strong style={{ color: "var(--ink)", fontWeight: 500 }}>{r.title}</strong><div className="course-tag" style={{ fontSize: 12 }}>{r.kind}</div></td>
                <td><div className="sp-bar"><div className="t"><b>{r.done} of {total}</b></div><div className="sp-track"><i className="sc" style={{ width: `${pct(r.done, total) || 0}%` }} /></div></div></td>
                <td><Bar value={r.avg} /></td>
                <td className="num">{r.pass == null ? "—" : `${r.pass}%`}</td>
                <td>{r.wait ? <Pill cls="watch">{r.wait} to grade</Pill> : <span className="course-tag">—</span>}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="course-tag">No quizzes or assignments yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Drawer({ s, data, onClose, onMark, onEmail }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onClose]);
  const L = { live: "Live", staff: "Marked by staff", rec: "Recording", absent: "Absent" };
  const g = fromGrading(s.grading, data.criteria);
  return (
    <>
      <div className="sp-scrim" onClick={onClose} />
      <aside className="sp-drawer" role="dialog" aria-modal="true" aria-label={s.name}>
        <header><Avatar s={s} /><div><h3>{s.name}</h3><small>{s.email} · {data.course.title} · {data.cohort.name}</small></div><button className="sp-x" type="button" onClick={onClose} aria-label="Close">✕</button></header>
        <div className="body">
          <div className="sp-acts"><Pill cls={STATUS[s.status][0]}>{STATUS[s.status][1]}</Pill>{s.ready && !s.certificate?.approved && <Pill cls="brand">Ready for certificate</Pill>}{s.certificate?.approved && <Pill cls="ok">Certificate issued</Pill>}{s.reasons.map((r) => <span key={r} className="sp-chip">{r}</span>)}</div>
          <div className="sp-mini">
            <div><b>{s.attendance.percent ?? "—"}{s.attendance.percent != null ? "%" : ""}</b><span>Attendance</span></div>
            <div><b>{s.activity ?? "—"}{s.activity != null ? "%" : ""}</b><span>Activities</span></div>
            <div><b>{s.points}</b><span>Points · {s.streak}-day streak</span></div>
            <div><b>{ago(s.idle_days)}</b><span>Last active</span></div>
          </div>
          <div><p className="sp-lbl">Toward the certificate</p>
            <CertificateProgress g={g} />
            <p className="course-tag" style={{ fontSize: 12.5, marginTop: 8 }}>
              Final activities score (every item, missing work as 0): <b>{g.activities.final ?? "—"}</b>{s.meets.activities ? " ✓" : ""}. This is what the certificate uses once the course ends.
            </p>
          </div>
          <div><p className="sp-lbl">What each piece of work counts</p><WorkTable g={g} /></div>
          <div><p className="sp-lbl">Classes</p>
            <div className="sp-cls">{data.classes.map((c, i) => ({ c, i })).reverse().map(({ c, i }) => (
              <span key={c.id} className={`sp-chip ${s.classes[i]}`} title={c.title}>{fmtDay(c.start_time)} · {L[s.classes[i]]}{s.zoom?.[i] != null ? ` · Zoom ${s.zoom[i]} min` : ""}
                {s.classes[i] === "absent" && <button type="button" onClick={() => onMark(s, c)}>Mark attended</button>}</span>
            ))}{!data.classes.length && <span className="course-tag">No classes held yet.</span>}</div>
          </div>
          <div className="sp-acts"><button className="cbtn go" type="button" onClick={() => onEmail([s])}>Email student</button>{s.last_emailed && <span className="course-tag" style={{ fontSize: 12.5 }}>Last emailed {fmtDay(s.last_emailed)}</span>}</div>
        </div>
      </aside>
    </>
  );
}

function MarkModal({ s, c, onCancel, onDone }) {
  const [reason, setReason] = useState("Connection problem during class");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/staff/attendance", { schedule_id: c.id, user_id: s.id, reason });
      toast.success(`${s.name} marked as attended`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.message || "We couldn't mark attendance. Please try again.");
      setBusy(false);
    }
  };
  return (
    <div className="sp-modal-wrap">
      <form className="sp-modal" role="dialog" aria-modal="true" aria-labelledby="mk-t" onSubmit={save}>
        <h3 id="mk-t">Mark {s.name} as attended?</h3>
        <p><b>{c.title}</b> · {fmtDay(c.start_time)}, {fmtTime(c.start_time)}. This counts toward their attendance and certificate, the same as joining live.</p>
        <label className="sp-field" htmlFor="mk-reason">Reason (saved with the record)
          <select id="mk-reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus>
            {["Connection problem during class", "Attended but couldn't mark attendance", "Joined from another account", "Other"].map((r) => <option key={r}>{r}</option>)}
          </select>
        </label>
        <div className="sp-warn">Make sure this is the right student and class.</div>
        <div className="sp-acts" style={{ justifyContent: "flex-end" }}>
          <button className="cbtn quiet" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button className="cbtn go" type="submit" disabled={busy}>{busy ? "Saving…" : "Yes, mark attended"}</button>
        </div>
      </form>
    </div>
  );
}

/** Student progress for one cohort-course (admins: any; instructors: their own). */
export default function StaffProgressPage() {
  const { authState: { data: auth } } = useContext(GlobalContext);
  const isAdmin = !!auth?.user?.is_admin;
  const { scopes, scope, setScope, failed: scopesFailed } = useScopes();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [marking, setMarking] = useState(null);
  const [emailTo, setEmailTo] = useState(null);

  const load = useCallback(() => {
    if (!scope || scope.all) return;
    setError(null);
    client.get("/staff/progress", { params: { cohort_id: scope.cohort_id, course_id: scope.course_id } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.message || "We couldn't load student progress."));
  }, [scope]);
  useEffect(() => { setData(null); load(); }, [load]);

  const student = useMemo(() => data?.students.find((s) => s.id === open), [data, open]);
  const counts = data ? { risk: data.students.filter((s) => s.status === "risk").length, wait: data.students.reduce((n, s) => n + s.submitted - s.graded, 0) } : {};

  return (
    <div className="lms"><div className="main single" style={{ maxWidth: 1240 }}><div className="view">
      <div className="sp-top">
        <div><span className="sp-kicker">{isAdmin ? "Learning" : "Teach"}</span><h1>{isAdmin ? "Student progress" : "My students"}</h1>
          <p>{scope?.all ? `All courses in ${scope.cohort} · compare courses, then pick one for detail` : data ? `${data.cohort.name.startsWith(data.course.title) ? data.cohort.name : `${data.course.title} · ${data.cohort.name}`} · ${data.students.length} students · ${data.classes.length} classes held` : "Attendance, scores and who needs help, per course."}</p></div>
        <div className="sp-filters"><ScopePicker scopes={scopes} scope={scope} setScope={setScope} allowAll /></div>
      </div>
      {scopesFailed ? <section className="complete-box"><div><strong>We couldn't load your courses</strong><small>Refresh the page to try again.</small></div></section>
        : scopes && !scopes.length ? <section className="complete-box"><div><strong>No courses to show</strong><small>{isAdmin ? "Add courses to a cohort first." : "You'll see your students once you're assigned to a course."}</small></div></section>
        : scope?.all ? <CohortProgress cohortId={scope.cohort_id} onOpenCourse={(courseId, studentId) => {
            const next = scopes.find((x) => x.cohort_id === scope.cohort_id && x.course_id === courseId);
            if (next) { setScope(next); setTab(studentId ? "students" : "overview"); setOpen(studentId || null); }
          }} />
        : error ? <section className="complete-box"><div><strong>{error}</strong><small>Check your connection and try again.</small></div><button className="cbtn quiet" type="button" onClick={load}>Try again</button></section>
        : !data ? <><div className="skeleton" style={{ height: 110 }} /><div className="skeleton" style={{ height: 320 }} /></>
        : (
          <>
            <nav className="tabs" role="tablist">
              {[["overview", "Overview"], ["students", "Students", counts.risk ? `${counts.risk} at risk` : null], ["classes", "Classes"], ["activities", "Activities", counts.wait ? `${counts.wait} to grade` : null]].map(([k, l, c]) => (
                <button key={k} className="tab" type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{l}{c && <span className="sp-pill brand" style={{ marginLeft: 6, padding: "1px 7px" }}>{c}</span>}</button>
              ))}
            </nav>
            {tab === "overview" && <Overview data={data} onOpen={setOpen} goStudents={() => { setFilter("risk"); setTab("students"); }} />}
            {tab === "students" && <Students data={data} onOpen={setOpen} filter={filter} setFilter={setFilter} onEmail={setEmailTo} />}
            {tab === "classes" && <Classes data={data} />}
            {tab === "activities" && <Activities data={data} />}
            {student && <Drawer s={student} data={data} onClose={() => setOpen(null)} onMark={(s, c) => setMarking({ s, c })} onEmail={setEmailTo} />}
            {emailTo && <EmailComposer scope={scope} course={data.course.title} recipients={emailTo} onCancel={() => setEmailTo(null)} onSent={() => { setEmailTo(null); load(); }} />}
            {marking && <MarkModal s={marking.s} c={marking.c} onCancel={() => setMarking(null)} onDone={() => { setMarking(null); load(); }} />}
          </>
        )}
      <p className="course-tag" style={{ display: "flex", gap: 6, alignItems: "center" }}><Icon name="info" size={14} />Status: Watch = one problem (attendance under 70%, activities score so far under 70, or inactive 7+ days). At risk = two or more, or missed the last 3 classes.</p>
    </div></div></div>
  );
}
